import { useState } from 'react';
import { Play, Pause, Zap, Receipt } from 'lucide-react';
import { palette } from '../../styles/tokens';
import { EmptyState } from '../common/EmptyState';
import type { RecurringInvoice } from '../../types';

interface RecurringTabProps {
  templates: RecurringInvoice[];
  toggleActive: (id: string) => Promise<void>;
  toggleAutoSend: (id: string) => Promise<void>;
  generateInvoice: (id: string) => Promise<boolean>;
  onNewRecurring: () => void;
}

function fmt(n: number): string {
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function freqLabel(f: string): string {
  const map: Record<string, string> = {
    weekly: 'Weekly', biweekly: 'Bi-weekly', monthly: 'Monthly', quarterly: 'Quarterly', annually: 'Annually',
  };
  return map[f] ?? f;
}

const thStyle: React.CSSProperties = {
  padding: '10px 12px', fontSize: 11.5, fontWeight: 600, color: palette.g500,
  textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'left',
  borderBottom: `1px solid ${palette.g150}`,
};
const tdStyle: React.CSSProperties = {
  padding: '10px 12px', fontSize: 13, color: palette.g700,
  borderBottom: `1px solid ${palette.g100}`,
};

export function RecurringTab({ templates, toggleActive, toggleAutoSend, generateInvoice, onNewRecurring }: RecurringTabProps) {
  const [generating, setGenerating] = useState<string | null>(null);

  async function handleGenerate(id: string) {
    setGenerating(id);
    await generateInvoice(id);
    setGenerating(null);
  }

  return (
    <div>
      {templates.length === 0 ? (
        <EmptyState icon={<Receipt size={22} />} title="No recurring invoices yet" subtitle="Set up automatic invoicing for repeat clients." actionLabel="New Recurring Invoice" onAction={onNewRecurring} />
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>Client</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Amount</th>
              <th style={thStyle}>Frequency</th>
              <th style={thStyle}>Next Invoice</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Auto-send</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {templates.map(t => (
              <tr key={t.id}
                style={{ transition: 'background 150ms ease' }}
                onMouseEnter={e => (e.currentTarget.style.background = '#F3F4F6')}
                onMouseLeave={e => (e.currentTarget.style.background = '')}>
                <td style={{ ...tdStyle, fontWeight: 600 }}>{t.client_name}</td>
                <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>${fmt(t.total)}</td>
                <td style={tdStyle}>{freqLabel(t.frequency)}</td>
                <td style={tdStyle}>{t.next_invoice_date}</td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>
                  <button
                    onClick={() => toggleAutoSend(t.id)}
                    style={{
                      padding: '2px 10px', borderRadius: 10, border: 'none',
                      background: t.auto_send ? palette.greenWash : palette.g100,
                      color: t.auto_send ? palette.green : palette.g400,
                      fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                    }}
                  >
                    {t.auto_send ? 'On' : 'Off'}
                  </button>
                </td>
                <td style={tdStyle}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 500,
                    background: t.is_active ? palette.greenWash : palette.g100,
                    color: t.is_active ? palette.green : palette.g400,
                  }}>
                    {t.is_active ? 'Active' : 'Paused'}
                  </span>
                </td>
                <td style={tdStyle}>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button
                      onClick={() => toggleActive(t.id)}
                      title={t.is_active ? 'Pause' : 'Resume'}
                      style={{
                        padding: '4px 8px', borderRadius: 4, border: `1px solid ${palette.g200}`,
                        background: palette.white, cursor: 'pointer', display: 'flex', alignItems: 'center',
                        color: palette.g600,
                      }}
                    >
                      {t.is_active ? <Pause size={12} /> : <Play size={12} />}
                    </button>
                    <button
                      onClick={() => handleGenerate(t.id)}
                      disabled={generating === t.id}
                      title="Generate Now"
                      style={{
                        display: 'flex', alignItems: 'center', gap: 4,
                        padding: '4px 10px', borderRadius: 4, border: 'none',
                        background: palette.accent, color: palette.white,
                        fontSize: 12, fontWeight: 600, cursor: generating === t.id ? 'wait' : 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >
                      <Zap size={11} /> {generating === t.id ? '...' : 'Generate'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
